import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { formatCurrency, formatDateTime, getTimeUntilExpiration } from '../../utils/formatters';
import StatusBadge from '../common/StatusBadge';
import { toast } from 'react-toastify';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { paymentAPI, greeterAPI } from '../../services/api';
import frontendPDFService from '../../services/pdfService';
import BookingAssignmentModal from './BookingAssignmentModal';
import { useAdminTheme } from '../../contexts/AdminThemeContext';

// Component for truncated description with expand/collapse
const TruncatedDescription = ({ description, maxWords = 2 }) => {
  const { isDarkMode } = useAdminTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!description) return null;
  
  const words = description.split(' ');
  const shouldTruncate = words.length > maxWords;
  const displayText = isExpanded || !shouldTruncate ? description : words.slice(0, maxWords).join(' ') + '...';
  
  return (
    <div className="max-w-xs">
      <div className={`text-sm transition-colors duration-300 ${
        isDarkMode ? 'text-gray-100' : 'text-gray-900'
      }`}>
        {displayText}
        {shouldTruncate && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`ml-1 text-xs font-medium transition-colors duration-300 ${
              isDarkMode 
                ? 'text-blue-400 hover:text-blue-300' 
                : 'text-blue-600 hover:text-blue-800'
            }`}
          >
            {isExpanded ? (
              <>
                <ChevronUpIcon className="w-3 h-3 inline" />
                <span className="ml-1">Less</span>
              </>
            ) : (
              <>
                <ChevronDownIcon className="w-3 h-3 inline" />
                <span className="ml-1">More</span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

const PaymentTable = ({ payments, onRefresh, onPaymentUpdate }) => {
  const { isDarkMode } = useAdminTheme();
  const location = useLocation();
  const [sendingBookingInvoice, setSendingBookingInvoice] = useState({});
  const [sendingPaymentConfirmation, setSendingPaymentConfirmation] = useState({});
  const [sendingPaymentLink, setSendingPaymentLink] = useState({});
  const [resendingPayment, setResendingPayment] = useState({});
  const [deletingPayment, setDeletingPayment] = useState({});
  const [sendingWhatsAppPaymentConfirmation, setSendingWhatsAppPaymentConfirmation] = useState({});
  const [sendingWhatsAppBookingConfirmation, setSendingWhatsAppBookingConfirmation] = useState({});
  const [sendingWhatsAppGreeterConfirmation, setSendingWhatsAppGreeterConfirmation] = useState({});
  const [assignmentModal, setAssignmentModal] = useState({ isOpen: false, booking: null });
  const [greeterInfoModal, setGreeterInfoModal] = useState({ isOpen: false, greeter: null });

  // Helper function to get source label and styling
  const getSourceInfo = (payment) => {
    const sourceType = payment.dataSource?.type || 'manual';
    
    const sourceMap = {
      'whatsapp_business': { label: 'WHATSAPP', color: 'green' },
      'unstructured_email': { label: 'EMAIL-AI', color: 'yellow' },
      'structured_email': { label: 'EMAIL-STR', color: 'blue' },
      'admin': { label: 'ADMIN', color: 'purple' },
      'user_dashboard': { label: 'USER', color: 'indigo' },
      'manual': { label: 'MANUAL', color: 'gray' }
    };
    
    return sourceMap[sourceType] || sourceMap['manual'];
  };

  // Close all dropdowns for a specific payment
  const closeAllDropdownsForPayment = (paymentId) => {
    const bookingDropdown = document.getElementById(`booking-dropdown-${paymentId}`);
    const paymentDropdown = document.getElementById(`payment-dropdown-${paymentId}`);
    const expiredDropdown = document.getElementById(`expired-dropdown-${paymentId}`);
    const whatsappDropdown = document.getElementById(`whatsapp-dropdown-${paymentId}`);
    
    if (bookingDropdown) bookingDropdown.classList.add('hidden');
    if (paymentDropdown) paymentDropdown.classList.add('hidden');
    if (expiredDropdown) expiredDropdown.classList.add('hidden');
    if (whatsappDropdown) whatsappDropdown.classList.add('hidden');
  };

  // Close all dropdowns for all payments
  const closeAllDropdowns = useCallback(() => {
    payments.forEach(payment => {
      closeAllDropdownsForPayment(payment._id);
    });
  }, [payments]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.booking-dropdown-container') && 
          !event.target.closest('.payment-dropdown-container') && 
          !event.target.closest('.expired-dropdown-container') &&
          !event.target.closest('.whatsapp-dropdown-container')) {
        closeAllDropdowns();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [payments, closeAllDropdowns]);

  const getEffectiveStatus = (payment) => {
    if (payment.status === 'pending') {
      const timeUntilExpiration = getTimeUntilExpiration(payment.expiresAt);
      return timeUntilExpiration.expired ? 'expired' : 'pending';
    }
    return payment.status;
  };

  const handleSendBookingInvoice = async (paymentId) => {
    setSendingBookingInvoice(prev => ({ ...prev, [paymentId]: true }));
    
    try {
      // Find the payment data
      const payment = payments.find(p => p._id === paymentId);
      if (!payment) {
        toast.error('Payment not found');
        return;
      }

      // Debug: Basic payment info (minimal logging)
      // console.log('🔍 PAYMENT TABLE DEBUG - handleSendBookingInvoice called for payment:', payment._id);
      
      // Add a small delay to ensure data is fully loaded (simulating debug log timing)
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Use PDF service to send booking invoice with PDF attachment
      const result = await frontendPDFService.sendBookingConfirmationEmail(
        payment, 
        payment.clientEmail,
        payment.serviceType || 'arrival', // Use actual service type from payment data
        paymentId // paymentRequestId for database update
      );
      
      if (result.success) {
        toast.success('Booking invoice sent successfully!');
        // Update the payment's booking invoice status locally
        if (onPaymentUpdate) {
          onPaymentUpdate(paymentId, { bookingInvoiceStatus: 'sent' });
        }
      } else {
        toast.error(result.message || 'Failed to send booking invoice');
      }
    } catch (error) {
      console.error('Error sending booking invoice:', error);
      toast.error('Failed to send booking invoice');
    } finally {
      setSendingBookingInvoice(prev => ({ ...prev, [paymentId]: false }));
    }
  };

  const handleSendPaymentConfirmation = async (paymentId) => {
    setSendingPaymentConfirmation(prev => ({ ...prev, [paymentId]: true }));
    
    try {
      // Find the payment data
      const payment = payments.find(p => p._id === paymentId);
      if (!payment) {
        toast.error('Payment not found');
        return;
      }

      // Use PDF service to send payment confirmation with PDF attachment
      const result = await frontendPDFService.sendPaymentConfirmationEmail(
        payment, 
        payment.clientEmail,
        paymentId // paymentRequestId for database update
      );
      
      if (result.success) {
        toast.success('Payment confirmation with invoice sent successfully!');
        // Update the payment's payment invoice status locally
        if (onPaymentUpdate) {
          onPaymentUpdate(paymentId, { paymentInvoiceStatus: 'sent' });
        }
      } else {
        toast.error(result.message || 'Failed to send payment confirmation');
      }
    } catch (error) {
      console.error('Error sending payment confirmation:', error);
      toast.error('Failed to send payment confirmation');
    } finally {
      setSendingPaymentConfirmation(prev => ({ ...prev, [paymentId]: false }));
    }
  };

  const handleSendPaymentLink = async (paymentId) => {
    setSendingPaymentLink(prev => ({ ...prev, [paymentId]: true }));
    
    try {
      const result = await paymentAPI.sendPaymentLink(paymentId);
      
      if (result.success) {
        toast.success('Payment link sent successfully!');
      } else {
        toast.error(result.message || 'Failed to send payment link');
      }
    } catch (error) {
      console.error('Error sending payment link:', error);
      toast.error('Failed to send payment link');
    } finally {
      setSendingPaymentLink(prev => ({ ...prev, [paymentId]: false }));
    }
  };

  const handleResendPayment = async (paymentId) => {
    setResendingPayment(prev => ({ ...prev, [paymentId]: true }));
    
    try {
      const result = await paymentAPI.resendPayment(paymentId);
      
      if (result.success) {
        toast.success('Payment request resent successfully!');
        if (onRefresh) {
          onRefresh(); // Refresh the payment list
        }
      } else {
        toast.error(result.message || 'Failed to resend payment request');
      }
    } catch (error) {
      console.error('Error resending payment:', error);
      toast.error('Failed to resend payment request');
    } finally {
      setResendingPayment(prev => ({ ...prev, [paymentId]: false }));
    }
  };

  const handleDeleteExpiredPayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this expired payment request? This action cannot be undone.')) {
      return;
    }

    setDeletingPayment(prev => ({ ...prev, [paymentId]: true }));
    
    try {
      const result = await paymentAPI.deletePayment(paymentId);
      
      if (result.success) {
        toast.success('Expired payment request deleted successfully!');
        if (onRefresh) {
          onRefresh(); // Refresh the payment list
        }
      } else {
        toast.error(result.message || 'Failed to delete payment request');
      }
    } catch (error) {
      console.error('Error deleting payment:', error);
      toast.error('Failed to delete payment request');
    } finally {
      setDeletingPayment(prev => ({ ...prev, [paymentId]: false }));
    }
  };

  const handleDownloadInvoice = async (payment) => {
    try {
      // Use frontend PDF service instead of backend
      await frontendPDFService.downloadPDF(payment, 'payment', `payment-invoice-${payment._id}.pdf`);
      toast.success('Payment invoice downloaded successfully!');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Failed to download payment invoice');
    }
  };

  // Handle assignment button click
  const handleAssignmentClick = async (payment) => {
    if (payment.isAssigned && payment.assignedGreeter) {
      // Show greeter info modal
      try {
        const response = await greeterAPI.getGreeterById(payment.assignedGreeter);
        if (response.success) {
          setGreeterInfoModal({ isOpen: true, greeter: response.data.greeter });
        } else {
          toast.error('Failed to fetch greeter information');
        }
      } catch (error) {
        console.error('Error fetching greeter info:', error);
        toast.error('Failed to fetch greeter information');
      }
    } else {
      // Open assignment modal
      setAssignmentModal({ isOpen: true, booking: payment });
    }
  };

  const handleSendWhatsAppPaymentConfirmation = async (paymentId) => {
    setSendingWhatsAppPaymentConfirmation(prev => ({ ...prev, [paymentId]: true }));
    
    try {
      const payment = payments.find(p => p._id === paymentId);
      if (!payment) {
        toast.error('Payment not found');
        return;
      }

      // Debug: Log the payment data structure
      console.log('🔍 Payment Data Structure Debug:');
      console.log('  Payment Object:', payment);
      console.log('  Available Keys:', Object.keys(payment));
      console.log('  clientPhone:', payment.clientPhone);
      console.log('  phoneNumber:', payment.phoneNumber);
      console.log('  serviceDetails:', payment.serviceDetails);

      // Extract phone number from payment data
      const phoneNumber = payment.clientPhone || payment.phoneNumber;
      if (!phoneNumber) {
        toast.error('Phone number not found for this payment');
        return;
      }

      const paymentData = {
        customerName: payment.clientName,
        serviceType: payment.serviceType,
        serviceLevel: payment.serviceDetails?.levelName || 'Standard',
        bookingDate: payment.createdAt,
        paymentAmount: payment.amount,
        paymentMethod: 'Stripe',
        confirmationNumber: payment.stripePaymentIntentId || payment._id
      };

      // Use the existing API service for proper authentication
      const { paymentAPI } = await import('../../services/api');
      
      const result = await paymentAPI.sendWhatsAppPaymentConfirmation(phoneNumber, paymentData);
      
      if (result.success) {
        toast.success('WhatsApp payment confirmation sent successfully!');
      } else {
        toast.error(result.message || 'Failed to send WhatsApp payment confirmation');
      }
    } catch (error) {
      console.error('Error sending WhatsApp payment confirmation:', error);
      toast.error('Failed to send WhatsApp payment confirmation');
    } finally {
      setSendingWhatsAppPaymentConfirmation(prev => ({ ...prev, [paymentId]: false }));
    }
  };

  const handleSendWhatsAppBookingConfirmation = async (paymentId) => {
    setSendingWhatsAppBookingConfirmation(prev => ({ ...prev, [paymentId]: true }));
    
    try {
      const payment = payments.find(p => p._id === paymentId);
      if (!payment) {
        toast.error('Payment not found');
        return;
      }

      const phoneNumber = payment.clientPhone || payment.phoneNumber;
      if (!phoneNumber) {
        toast.error('Phone number not found for this payment');
        return;
      }

      const bookingData = {
        customerName: payment.clientName,
        serviceType: payment.serviceType,
        serviceLevel: payment.serviceDetails?.levelName || 'Standard',
        bookingDate: payment.createdAt,
        flightDetails: payment.flightDetails,
        greeterName: payment.assignedGreeter?.firstName + ' ' + payment.assignedGreeter?.lastName,
        greeterContact: payment.assignedGreeter?.phoneNumber
      };

      // Use the existing API service for proper authentication
      const { paymentAPI } = await import('../../services/api');
      
      const result = await paymentAPI.sendWhatsAppBookingConfirmation(phoneNumber, bookingData);
      
      if (result.success) {
        toast.success('WhatsApp booking confirmation sent successfully!');
      } else {
        toast.error(result.message || 'Failed to send WhatsApp booking confirmation');
      }
    } catch (error) {
      console.error('Error sending WhatsApp booking confirmation:', error);
      toast.error('Failed to send WhatsApp booking confirmation');
    } finally {
      setSendingWhatsAppBookingConfirmation(prev => ({ ...prev, [paymentId]: false }));
    }
  };

  const handleSendWhatsAppGreeterConfirmation = async (paymentId) => {
    setSendingWhatsAppGreeterConfirmation(prev => ({ ...prev, [paymentId]: true }));
    
    try {
      const payment = payments.find(p => p._id === paymentId);
      if (!payment) {
        toast.error('Payment not found');
        return;
      }

      // Check if payment has assigned greeter
      if (!payment.isAssigned || !payment.assignedGreeter) {
        toast.error('No greeter assigned to this payment');
        return;
      }

      // Use the client's phone number from the payment request
      const phoneNumber = payment.clientPhone || payment.phoneNumber;
      if (!phoneNumber) {
        toast.error('Client phone number not found for this payment');
        return;
      }

      // Fetch greeter details to get proper name and contact
      let greeter = payment.assignedGreeter;
      
      // If assignedGreeter is just an ID, fetch the greeter details
      if (typeof payment.assignedGreeter === 'string' || !payment.assignedGreeter.firstName) {
        try {
          const greeterResponse = await greeterAPI.getGreeterById(payment.assignedGreeter);
          if (greeterResponse.success) {
            greeter = greeterResponse.data.greeter;
          }
        } catch (greeterError) {
          console.error('Error fetching greeter details:', greeterError);
          toast.error('Failed to fetch greeter details');
          return;
        }
      }

      // Extract service date and time from flightDetails or use createdAt
      const flightDate = payment.flightDetails?.departureDate || payment.flightDetails?.arrivalDate;
      let serviceDate = '';
      let serviceTime = 'TBD';
      
      if (flightDate) {
        const dateObj = new Date(flightDate);
        serviceDate = dateObj.toLocaleDateString('en-GB', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
        serviceTime = dateObj.toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit'
        });
      } else if (payment.createdAt) {
        const dateObj = new Date(payment.createdAt);
        serviceDate = dateObj.toLocaleDateString('en-GB', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
        serviceTime = dateObj.toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit'
        });
      }

      const greeterData = {
        customerName: payment.clientName,
        serviceType: payment.serviceType,
        serviceDate: serviceDate,
        serviceTime: serviceTime,
        greeterName: greeter.firstName + ' ' + greeter.lastName,
        greeterContact: greeter.phoneNumber,
        greeterEmail: greeter.email
      };

      
      const result = await paymentAPI.sendWhatsAppGreeterConfirmation(phoneNumber, greeterData);
      
      if (result.success) {
        toast.success('WhatsApp greeter confirmation sent successfully!');
      } else {
        toast.error(result.message || 'Failed to send WhatsApp greeter confirmation');
      }
    } catch (error) {
      console.error('Error sending WhatsApp greeter confirmation:', error);
      toast.error('Failed to send WhatsApp greeter confirmation');
    } finally {
      setSendingWhatsAppGreeterConfirmation(prev => ({ ...prev, [paymentId]: false }));
    }
  };


  return (
    <div className={isDarkMode ? 'card-dark overflow-hidden' : 'card overflow-hidden'}>
      <div className="overflow-x-auto">
        <table className={isDarkMode ? 'table-dark' : 'table'}>
          <thead className={isDarkMode ? 'table-header-dark' : 'table-header'}>
            <tr>
              <th className={isDarkMode ? 'table-header-cell-dark' : 'table-header-cell'}>Client</th>
              <th className={isDarkMode ? 'table-header-cell-dark' : 'table-header-cell'}>Source</th>
              <th className={isDarkMode ? 'table-header-cell-dark' : 'table-header-cell'}>Service Type</th>
              <th className={isDarkMode ? 'table-header-cell-dark' : 'table-header-cell'}>Amount</th>
              <th className={isDarkMode ? 'table-header-cell-dark' : 'table-header-cell'}>Status</th>
              <th className={isDarkMode ? 'table-header-cell-dark' : 'table-header-cell'}>Actions</th>
              <th className={isDarkMode ? 'table-header-cell-dark' : 'table-header-cell'}>Description</th>
              <th className={isDarkMode ? 'table-header-cell-dark' : 'table-header-cell'}>Created</th>
              <th className={isDarkMode ? 'table-header-cell-dark' : 'table-header-cell'}>Expires</th>
            </tr>
          </thead>
          <tbody className={isDarkMode ? 'table-body-dark' : 'table-body'}>
            {payments.map((payment) => {
              const effectiveStatus = getEffectiveStatus(payment);
              const timeUntilExpiration = getTimeUntilExpiration(payment.expiresAt);
              
              return (
                <tr key={payment._id} className={`transition-colors duration-300 ${
                  isDarkMode ? 'hover:bg-skyone-inset-dark' : 'hover:bg-gray-50'
                }`}>
                  {/* Client Info */}
                  <td className={isDarkMode ? 'table-cell-dark' : 'table-cell'}>
                    <div>
                      <div className={`font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-100' : 'text-gray-900'
                      }`}>
                        {payment.clientName}
                      </div>
                      <div className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {payment.clientEmail}
                      </div>
                    </div>
                  </td>

                  {/* Source */}
                  <td className={isDarkMode ? 'table-cell-dark' : 'table-cell'}>
                    {(() => {
                      const sourceInfo = getSourceInfo(payment);
                      return (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${
                          sourceInfo.color === 'green'
                            ? (isDarkMode ? 'bg-green-900/40 text-green-300 border border-green-700' : 'bg-green-100 text-green-800')
                            : sourceInfo.color === 'yellow'
                            ? (isDarkMode ? 'bg-yellow-900/40 text-yellow-300 border border-yellow-700' : 'bg-yellow-100 text-yellow-800')
                            : sourceInfo.color === 'blue'
                            ? (isDarkMode ? 'bg-blue-900/40 text-blue-300 border border-blue-700' : 'bg-blue-100 text-blue-800')
                            : sourceInfo.color === 'purple'
                            ? (isDarkMode ? 'bg-purple-900/40 text-purple-300 border border-purple-700' : 'bg-purple-100 text-purple-800')
                            : sourceInfo.color === 'indigo'
                            ? (isDarkMode ? 'bg-indigo-900/40 text-indigo-300 border border-indigo-700' : 'bg-indigo-100 text-indigo-800')
                            : (isDarkMode ? 'bg-skyone-inset-dark/90 text-gray-300 border border-skyone-border-dark' : 'bg-gray-100 text-gray-800')
                        }`}>
                          {sourceInfo.label}
                        </span>
                      );
                    })()}
                  </td>

                  {/* Service Type */}
                  <td className={isDarkMode ? 'table-cell-dark' : 'table-cell'}>
                    <div className="max-w-xs">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors duration-300 ${
                        payment.serviceType === 'ARRIVAL' 
                          ? (isDarkMode ? 'bg-blue-900/40 text-blue-300 border border-blue-700' : 'bg-blue-100 text-blue-800')
                          : payment.serviceType === 'DEPARTURE' 
                            ? (isDarkMode ? 'bg-green-900/40 text-green-300 border border-green-700' : 'bg-green-100 text-green-800')
                            : (isDarkMode ? 'bg-yellow-900/40 text-yellow-300 border border-yellow-700' : 'bg-yellow-100 text-yellow-800')
                      }`}>
                        {payment.serviceType || 'ARRIVAL'}
                      </span>
                    </div>
                  </td>

                  {/* Amount */}
                  <td className={isDarkMode ? 'table-cell-dark' : 'table-cell'}>
                    <div className={`font-medium transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>
                      {formatCurrency(payment.amount, payment.currency)}
                    </div>
                  </td>

                  {/* Status */}
                  <td className={isDarkMode ? 'table-cell-dark' : 'table-cell'}>
                    <StatusBadge status={effectiveStatus} showIcon={true} />
                    {effectiveStatus === 'pending' && !timeUntilExpiration.expired && (
                      <div className={`text-xs mt-1 transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {timeUntilExpiration.hours}h {timeUntilExpiration.minutes}m left
                      </div>
                    )}
                  </td>

                  {/* Actions */}
                  <td className={isDarkMode ? 'table-cell-dark' : 'table-cell'}>
                    <div className="flex items-center space-x-3">
                      {/* Primary Actions */}
                      {payment.paymentLink && effectiveStatus === 'pending' && !timeUntilExpiration.expired && (
                        <div className="relative inline-block text-left">
                          <div className="flex items-center space-x-2">
                            <a
                              href={payment.paymentLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`inline-flex items-center px-3 py-2 text-xs font-semibold border rounded-lg transition-all duration-200 shadow-sm ${
                                isDarkMode
                                  ? 'text-green-300 bg-green-900/40 border-green-700 hover:bg-green-900/60 hover:text-green-200'
                                  : 'text-green-700 bg-green-100 border-green-300 hover:bg-green-200 hover:text-green-800'
                              }`}
                            >
                              <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                              </svg>
                              Pay
                            </a>
                            <button
                              onClick={() => handleSendPaymentLink(payment._id)}
                              disabled={sendingPaymentLink[payment._id]}
                              className={`inline-flex items-center px-3 py-2 text-xs font-semibold border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm ${
                                isDarkMode
                                  ? 'text-blue-300 bg-blue-900/40 border-blue-700 hover:bg-blue-900/60 hover:text-blue-200'
                                  : 'text-blue-700 bg-blue-100 border-blue-300 hover:bg-blue-200 hover:text-blue-800'
                              }`}
                            >
                              <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              {sendingPaymentLink[payment._id] ? 'Sending...' : 'Send Link'}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Assignment Button for Paid Payments */}
                      {effectiveStatus === 'paid' && (
                        <button
                          onClick={() => handleAssignmentClick(payment)}
                          className={`inline-flex items-center px-3 py-2 text-xs font-semibold rounded-lg transition-all duration-200 shadow-sm ${
                            payment.isAssigned
                              ? (isDarkMode 
                                  ? 'text-green-300 bg-green-900/40 border border-green-700 hover:bg-green-900/60 hover:text-green-200'
                                  : 'text-green-700 bg-green-100 border border-green-300 hover:bg-green-200 hover:text-green-800')
                              : (isDarkMode
                                  ? 'text-red-300 bg-red-900/40 border border-red-700 hover:bg-red-900/60 hover:text-red-200'
                                  : 'text-red-700 bg-red-100 border border-red-300 hover:bg-red-200 hover:text-red-800')
                          }`}
                        >
                          <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          {payment.isAssigned ? 'Assigned' : 'Not Assigned'}
                        </button>
                      )}

                      {/* Booking Actions Dropdown for Paid Payments */}
                      {effectiveStatus === 'paid' && (
                        <div className="relative inline-block text-left booking-dropdown-container">
                          <div className="flex items-center space-x-2">
                            <button
                              type="button"
                              className={`inline-flex items-center px-3 py-2 text-xs font-semibold border rounded-lg transition-all duration-200 shadow-sm ${
                                isDarkMode
                                  ? 'text-purple-300 bg-purple-900/40 border-purple-700 hover:bg-purple-900/60 hover:text-purple-200'
                                  : 'text-purple-700 bg-purple-100 border-purple-300 hover:bg-purple-200 hover:text-purple-800'
                              }`}
                              onClick={() => {
                                // Close all other dropdowns first
                                closeAllDropdowns();
                                // Then toggle the booking dropdown
                                const dropdown = document.getElementById(`booking-dropdown-${payment._id}`);
                                dropdown.classList.toggle('hidden');
                              }}
                            >
                              <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Booking
                              <svg className="w-3 h-3 ml-1.5 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            
                            {/* Booking invoice status indicator */}
                            <div className="flex items-center">
                              <div 
                                className={`w-3 h-3 rounded-full border-2 shadow-sm ${
                                  payment.bookingInvoiceStatus === 'sent' 
                                    ? 'bg-green-500 border-green-600' 
                                    : 'bg-red-500 border-red-600'
                                }`}
                                title={payment.bookingInvoiceStatus === 'sent' ? 'Booking invoice sent' : 'Booking invoice not sent'}
                              ></div>
                            </div>
                            
                            {/* Small loader during booking invoice sending */}
                            {sendingBookingInvoice[payment._id] && (
                              <div className="flex items-center">
                                <div className="w-4 h-4 border-2 border-gray-300 border-t-purple-600 rounded-full animate-spin"></div>
                              </div>
                            )}
                          </div>
                          
                          <div
                            id={`booking-dropdown-${payment._id}`}
                            className={`hidden absolute right-0 z-20 mt-2 w-56 origin-top-right rounded-xl shadow-xl ring-1 ring-opacity-5 focus:outline-none border transition-colors duration-300 ${
                              isDarkMode
                                ? 'bg-skyone-card-dark ring-skyone-border-dark border-skyone-border-dark'
                                : 'bg-white ring-black border-gray-100'
                            }`}
                          >
                            <div className="py-2">
                              <div className={`px-4 py-2 border-b transition-colors duration-300 ${
                                isDarkMode ? 'border-skyone-border-dark' : 'border-gray-100'
                              }`}>
                                <h3 className={`text-xs font-semibold uppercase tracking-wide transition-colors duration-300 ${
                                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                                }`}>Booking Confirmation</h3>
                              </div>
                              <button
                                onClick={async () => {
                                  closeAllDropdownsForPayment(payment._id);
                                  try {
                                    const previewUrl = await frontendPDFService.getPDFPreviewURL(payment, payment.serviceType?.toLowerCase() || 'arrival');
                                    window.open(previewUrl, '_blank');
                                  } catch (error) {
                                    console.error('Error viewing PDF:', error);
                                    toast.error('Failed to view PDF');
                                  }
                                }}
                                className={`flex items-center px-4 py-3 text-sm transition-colors w-full text-left ${
                                  isDarkMode
                                    ? 'text-gray-200 hover:bg-blue-900/40 hover:text-blue-300'
                                    : 'text-gray-700 hover:bg-blue-50 hover:text-blue-700'
                                }`}
                              >
                                <svg className={`w-4 h-4 mr-3 transition-colors duration-300 ${
                                  isDarkMode ? 'text-blue-400' : 'text-blue-500'
                                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                View Booking PDF
                              </button>
                              <button
                                onClick={async () => {
                                  closeAllDropdownsForPayment(payment._id);
                                  try {
                                    await frontendPDFService.downloadPDF(payment, payment.serviceType?.toLowerCase() || 'arrival', `booking-invoice-${payment._id}.pdf`);
                                    toast.success('Booking invoice downloaded successfully!');
                                  } catch (error) {
                                    console.error('Error downloading booking invoice:', error);
                                    toast.error('Failed to download booking invoice');
                                  }
                                }}
                                className={`flex items-center px-4 py-3 text-sm transition-colors w-full text-left ${
                                  isDarkMode
                                    ? 'text-gray-200 hover:bg-green-900/40 hover:text-green-300'
                                    : 'text-gray-700 hover:bg-green-50 hover:text-green-700'
                                }`}
                              >
                                <svg className={`w-4 h-4 mr-3 transition-colors duration-300 ${
                                  isDarkMode ? 'text-green-400' : 'text-green-500'
                                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Download Booking PDF
                              </button>
                              <button
                                onClick={() => {
                                  handleSendBookingInvoice(payment._id);
                                  closeAllDropdownsForPayment(payment._id);
                                }}
                                disabled={sendingBookingInvoice[payment._id]}
                                className={`flex items-center w-full px-4 py-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                                  isDarkMode
                                    ? 'text-gray-200 hover:bg-red-900/40 hover:text-red-300'
                                    : 'text-gray-700 hover:bg-red-50 hover:text-red-700'
                                }`}
                              >
                                <svg className="w-4 h-4 mr-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                {sendingBookingInvoice[payment._id] ? 'Sending...' : 'Send Booking Invoice'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Payment Actions Dropdown for Paid Payments */}
                      {effectiveStatus === 'paid' && (
                        <div className="relative inline-block text-left payment-dropdown-container">
                          <div className="flex items-center space-x-2">
                            <button
                              type="button"
                              className={`inline-flex items-center px-3 py-2 text-xs font-semibold border rounded-lg transition-all duration-200 shadow-sm ${
                                isDarkMode
                                  ? 'text-green-300 bg-green-900/40 border-green-700 hover:bg-green-900/60 hover:text-green-200'
                                  : 'text-green-700 bg-green-100 border-green-300 hover:bg-green-200 hover:text-green-800'
                              }`}
                              onClick={() => {
                                // Close all other dropdowns first
                                closeAllDropdowns();
                                // Then toggle the payment dropdown
                                const dropdown = document.getElementById(`payment-dropdown-${payment._id}`);
                                dropdown.classList.toggle('hidden');
                              }}
                            >
                              <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                              </svg>
                              Payment
                              <svg className="w-3 h-3 ml-1.5 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            
                            {/* Payment invoice status indicator */}
                            <div className="flex items-center">
                              <div 
                                className={`w-3 h-3 rounded-full border-2 shadow-sm ${
                                  payment.paymentInvoiceStatus === 'sent' 
                                    ? 'bg-green-500 border-green-600' 
                                    : 'bg-red-500 border-red-600'
                                }`}
                                title={payment.paymentInvoiceStatus === 'sent' ? 'Payment confirmation sent' : 'Payment confirmation not sent'}
                              ></div>
                            </div>
                            
                            {/* Small loader during payment confirmation sending */}
                            {sendingPaymentConfirmation[payment._id] && (
                              <div className="flex items-center">
                                <div className="w-4 h-4 border-2 border-gray-300 border-t-green-600 rounded-full animate-spin"></div>
                              </div>
                            )}
                          </div>
                          
                          <div
                            id={`payment-dropdown-${payment._id}`}
                            className={`hidden absolute right-0 z-20 mt-2 w-64 origin-top-right rounded-xl shadow-xl ring-1 ring-opacity-5 focus:outline-none border transition-colors duration-300 ${
                              isDarkMode
                                ? 'bg-skyone-card-dark ring-skyone-border-dark border-skyone-border-dark'
                                : 'bg-white ring-black border-gray-100'
                            }`}
                          >
                            <div className="py-2">
                              <div className={`px-4 py-2 border-b transition-colors duration-300 ${
                                isDarkMode ? 'border-skyone-border-dark' : 'border-gray-100'
                              }`}>
                                <h3 className={`text-xs font-semibold uppercase tracking-wide transition-colors duration-300 ${
                                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                                }`}>Payment Management</h3>
                              </div>
                              <Link
                                to={`/admin/payment/${payment._id}`}
                                state={{ from: location.pathname }}
                                className={`flex items-center px-4 py-3 text-sm transition-colors ${
                                  isDarkMode
                                    ? 'text-gray-200 hover:bg-blue-900/40 hover:text-blue-300'
                                    : 'text-gray-700 hover:bg-blue-50 hover:text-blue-700'
                                }`}
                                onClick={() => {
                                  closeAllDropdownsForPayment(payment._id);
                                }}
                              >
                                <svg className={`w-4 h-4 mr-3 transition-colors duration-300 ${
                                  isDarkMode ? 'text-blue-400' : 'text-blue-500'
                                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                View Payment Details
                              </Link>
                              <button
                                onClick={() => {
                                  handleSendPaymentConfirmation(payment._id);
                                  closeAllDropdownsForPayment(payment._id);
                                }}
                                disabled={sendingPaymentConfirmation[payment._id]}
                                className={`flex items-center w-full px-4 py-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                                  isDarkMode
                                    ? 'text-gray-200 hover:bg-green-900/40 hover:text-green-300'
                                    : 'text-gray-700 hover:bg-green-50 hover:text-green-700'
                                }`}
                              >
                                <svg className="w-4 h-4 mr-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                {sendingPaymentConfirmation[payment._id] ? 'Sending...' : 'Send Payment Confirmation'}
                              </button>
                              <button
                                onClick={() => {
                                  handleDownloadInvoice(payment);
                                  closeAllDropdownsForPayment(payment._id);
                                }}
                                className={`flex items-center w-full px-4 py-3 text-sm transition-colors ${
                                  isDarkMode
                                    ? 'text-gray-200 hover:bg-purple-900/40 hover:text-purple-300'
                                    : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                                }`}
                              >
                                <svg className={`w-4 h-4 mr-3 transition-colors duration-300 ${
                                  isDarkMode ? 'text-purple-400' : 'text-purple-500'
                                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Download Payment Invoice
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* WhatsApp Actions Dropdown for Paid Payments */}
                      {effectiveStatus === 'paid' && (
                        <div className="relative inline-block text-left whatsapp-dropdown-container">
                          <div className="flex items-center space-x-2">
                            <button
                              type="button"
                              className={`inline-flex items-center px-3 py-2 text-xs font-semibold border rounded-lg transition-all duration-200 shadow-sm ${
                                isDarkMode
                                  ? 'text-green-300 bg-green-900/40 border-green-700 hover:bg-green-900/60 hover:text-green-200'
                                  : 'text-green-700 bg-green-100 border-green-300 hover:bg-green-200 hover:text-green-800'
                              }`}
                              onClick={() => {
                                // Close all other dropdowns first
                                closeAllDropdowns();
                                // Then toggle the WhatsApp dropdown
                                const dropdown = document.getElementById(`whatsapp-dropdown-${payment._id}`);
                                dropdown.classList.toggle('hidden');
                              }}
                            >
                              <svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                              </svg>
                              WhatsApp
                              <svg className="w-3 h-3 ml-1.5 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          </div>
                          
                          <div
                            id={`whatsapp-dropdown-${payment._id}`}
                            className={`hidden absolute right-0 z-20 mt-2 w-64 origin-top-right rounded-xl shadow-xl ring-1 ring-opacity-5 focus:outline-none border transition-colors duration-300 ${
                              isDarkMode
                                ? 'bg-skyone-card-dark ring-skyone-border-dark border-skyone-border-dark'
                                : 'bg-white ring-black border-gray-100'
                            }`}
                          >
                            <div className="py-2">
                              <div className={`px-4 py-2 border-b transition-colors duration-300 ${
                                isDarkMode ? 'border-skyone-border-dark' : 'border-gray-100'
                              }`}>
                                <h3 className={`text-xs font-semibold uppercase tracking-wide transition-colors duration-300 ${
                                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                                }`}>WhatsApp Messages</h3>
                              </div>
                              <button
                                onClick={() => {
                                  handleSendWhatsAppPaymentConfirmation(payment._id);
                                  closeAllDropdownsForPayment(payment._id);
                                }}
                                disabled={sendingWhatsAppPaymentConfirmation[payment._id]}
                                className={`flex items-center w-full px-4 py-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                                  isDarkMode
                                    ? 'text-gray-200 hover:bg-green-900/40 hover:text-green-300'
                                    : 'text-gray-700 hover:bg-green-50 hover:text-green-700'
                                }`}
                              >
                                <svg className="w-4 h-4 mr-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {sendingWhatsAppPaymentConfirmation[payment._id] ? 'Sending...' : 'Send Payment Confirmation'}
                              </button>
                              <button
                                onClick={() => {
                                  handleSendWhatsAppBookingConfirmation(payment._id);
                                  closeAllDropdownsForPayment(payment._id);
                                }}
                                disabled={sendingWhatsAppBookingConfirmation[payment._id]}
                                className={`flex items-center w-full px-4 py-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                                  isDarkMode
                                    ? 'text-gray-200 hover:bg-blue-900/40 hover:text-blue-300'
                                    : 'text-gray-700 hover:bg-blue-50 hover:text-blue-700'
                                }`}
                              >
                                <svg className="w-4 h-4 mr-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                {sendingWhatsAppBookingConfirmation[payment._id] ? 'Sending...' : 'Send Booking Confirmation'}
                              </button>
                              <button
                                onClick={() => {
                                  handleSendWhatsAppGreeterConfirmation(payment._id);
                                  closeAllDropdownsForPayment(payment._id);
                                }}
                                disabled={sendingWhatsAppGreeterConfirmation[payment._id]}
                                className={`flex items-center w-full px-4 py-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                                  isDarkMode
                                    ? 'text-gray-200 hover:bg-purple-900/40 hover:text-purple-300'
                                    : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                                }`}
                              >
                                <svg className="w-4 h-4 mr-3 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                                {sendingWhatsAppGreeterConfirmation[payment._id] ? 'Sending...' : 'Send Greeter Confirmation'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Stripe Link */}
                      {payment.stripePaymentIntentId && effectiveStatus === 'paid' && (
                        <a
                          href={`https://dashboard.stripe.com/payments/${payment.stripePaymentIntentId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center px-2.5 py-1.5 text-xs font-medium border rounded-md transition-colors ${
                            isDarkMode
                              ? 'text-blue-300 bg-blue-900/40 border-blue-700 hover:bg-blue-900/60 hover:text-blue-200'
                              : 'text-blue-600 bg-blue-50 border-blue-200 hover:bg-blue-100 hover:text-blue-700'
                          }`}
                          title="View in Stripe Dashboard"
                        >
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.274 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.407-2.354 1.407-1.675 0-3.868-.921-5.994-2.07l-.89 5.494C5.576 23.73 8.224 24 11.857 24c2.532 0 4.633-.624 6.095-1.801 1.512-1.127 2.346-2.915 2.346-5.072 0-4.128-2.524-5.851-6.322-6.977z"/>
                          </svg>
                          Stripe
                        </a>
                      )}

                      {/* Expired Payment Actions Dropdown */}
                      {effectiveStatus === 'expired' && (
                        <div className="relative inline-block text-left expired-dropdown-container">
                          <div>
                            <button
                              type="button"
                              className={`inline-flex items-center px-3 py-2 text-xs font-semibold border rounded-lg transition-all duration-200 shadow-sm ${
                                isDarkMode
                                  ? 'text-red-300 bg-red-900/40 border-red-700 hover:bg-red-900/60 hover:text-red-200'
                                  : 'text-red-700 bg-red-100 border-red-300 hover:bg-red-200 hover:text-red-800'
                              }`}
                              onClick={() => {
                                // Close all other dropdowns first
                                closeAllDropdowns();
                                // Then toggle the expired dropdown
                                const dropdown = document.getElementById(`expired-dropdown-${payment._id}`);
                                dropdown.classList.toggle('hidden');
                              }}
                            >
                              <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Expired
                              <svg className="w-3 h-3 ml-1.5 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          </div>
                          
                          <div
                            id={`expired-dropdown-${payment._id}`}
                            className={`hidden absolute right-0 z-20 mt-2 w-56 origin-top-right rounded-xl shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none transition-colors duration-300 ${
                              isDarkMode
                                ? 'bg-skyone-card-dark border border-skyone-border-dark'
                                : 'bg-white border border-gray-100'
                            }`}
                          >
                            <div className="py-2">
                              <div className={`px-4 py-2 border-b transition-colors duration-300 ${
                                isDarkMode ? 'border-skyone-border-dark' : 'border-gray-100'
                              }`}>
                                <h3 className={`text-xs font-semibold uppercase tracking-wide transition-colors duration-300 ${
                                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                                }`}>Expired Payment</h3>
                              </div>
                              <button
                                onClick={() => {
                                  handleResendPayment(payment._id);
                                  closeAllDropdownsForPayment(payment._id);
                                }}
                                disabled={resendingPayment[payment._id]}
                                className={`flex items-center w-full px-4 py-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                                  isDarkMode
                                    ? 'text-gray-300 hover:bg-orange-900/40 hover:text-orange-300'
                                    : 'text-gray-700 hover:bg-orange-50 hover:text-orange-700'
                                }`}
                              >
                                <svg className="w-4 h-4 mr-3 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                {resendingPayment[payment._id] ? 'Resending...' : 'Resend Payment'}
                              </button>
                              <button
                                onClick={() => {
                                  handleDeleteExpiredPayment(payment._id);
                                  closeAllDropdownsForPayment(payment._id);
                                }}
                                disabled={deletingPayment[payment._id]}
                                className={`flex items-center w-full px-4 py-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                                  isDarkMode
                                    ? 'text-gray-300 hover:bg-red-900/40 hover:text-red-300'
                                    : 'text-gray-700 hover:bg-red-50 hover:text-red-700'
                                }`}
                              >
                                <svg className="w-4 h-4 mr-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                {deletingPayment[payment._id] ? 'Deleting...' : 'Delete Payment'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Description */}
                  <td className={isDarkMode ? 'table-cell-dark' : 'table-cell'}>
                    <TruncatedDescription description={payment.description} maxWords={2} />
                  </td>

                  {/* Created Date */}
                  <td className={isDarkMode ? 'table-cell-dark' : 'table-cell'}>
                    <div className={`transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>
                      {formatDateTime(payment.createdAt)}
                    </div>
                  </td>

                  {/* Expires Date */}
                  <td className={isDarkMode ? 'table-cell-dark' : 'table-cell'}>
                    <div className={`text-sm transition-colors duration-300 ${
                      timeUntilExpiration.expired 
                        ? (isDarkMode ? 'text-red-400' : 'text-red-600')
                        : (isDarkMode ? 'text-gray-100' : 'text-gray-900')
                    }`}>
                      {formatDateTime(payment.expiresAt)}
                    </div>
                    {effectiveStatus === 'pending' && !timeUntilExpiration.expired && (
                      <div className={`text-xs transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {timeUntilExpiration.hours}h {timeUntilExpiration.minutes}m left
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Booking Assignment Modal */}
      <BookingAssignmentModal
        isOpen={assignmentModal.isOpen}
        onClose={() => setAssignmentModal({ isOpen: false, booking: null })}
        booking={assignmentModal.booking}
        onAssignmentSuccess={(assignmentData) => {
          // Update the payment with assignment info if needed
          if (onPaymentUpdate) {
            onPaymentUpdate(assignmentModal.booking._id, {
              assignedGreeter: assignmentData.greeter,
              assignedAt: new Date().toISOString()
            });
          }
          // Refresh the payments list
          if (onRefresh) {
            onRefresh();
          }
        }}
      />

      {/* Greeter Info Modal */}
      {greeterInfoModal.isOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className={`relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md transition-colors duration-300 ${
            isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
          }`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-lg font-semibold transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Assigned Greeter
              </h3>
              <button
                onClick={() => setGreeterInfoModal({ isOpen: false, greeter: null })}
                className={`transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {greeterInfoModal.greeter && (
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 h-12 w-12">
                    <div className={`h-12 w-12 rounded-full flex items-center justify-center transition-colors duration-300 ${
                      isDarkMode ? 'bg-blue-900/20' : 'bg-primary-100'
                    }`}>
                      <svg className={`h-6 w-6 transition-colors duration-300 ${
                        isDarkMode ? 'text-blue-400' : 'text-primary-600'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                  </div>
                  <div>
                    <div className={`text-lg font-medium transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {greeterInfoModal.greeter.firstName} {greeterInfoModal.greeter.lastName}
                    </div>
                    <div className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {greeterInfoModal.greeter.email}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center">
                    <svg className={`h-4 w-4 mr-2 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-400'
                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>{greeterInfoModal.greeter.phoneNumber}</span>
                  </div>
                  
                  <div className="flex items-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors duration-300 ${
                      greeterInfoModal.greeter.isActive 
                        ? isDarkMode 
                          ? 'bg-green-900/20 text-green-400' 
                          : 'bg-green-100 text-green-800'
                        : isDarkMode
                          ? 'bg-red-900/20 text-red-400'
                          : 'bg-red-100 text-red-800'
                    }`}>
                      {greeterInfoModal.greeter.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  <div className="flex items-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors duration-300 ${
                      greeterInfoModal.greeter.isAvailable && greeterInfoModal.greeter.isActive
                        ? isDarkMode 
                          ? 'bg-green-900/20 text-green-400' 
                          : 'bg-green-100 text-green-800'
                        : isDarkMode
                          ? 'bg-red-900/20 text-red-400'
                          : 'bg-red-100 text-red-800'
                    }`}>
                      {greeterInfoModal.greeter.isAvailable && greeterInfoModal.greeter.isActive ? 'Available' : 'Unavailable'}
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setGreeterInfoModal({ isOpen: false, greeter: null })}
                className={`px-4 py-2 rounded-md transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-skyone-inset-dark text-zinc-300 hover:bg-skyone-border-dark' 
                    : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentTable;
